import { FormHelperText, Stack } from '@mui/material';
import { formatMembershipName } from '@provision/service-level-utils';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormSelect } from 'src/components/Forms/FormSelect/form-select';
import { FormTextField } from 'src/components/Forms/FormTextField/form-text-field';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { useOfferedProducts } from 'src/graphql';
import { useStripeSubscriptionSchedule } from 'src/graphql/entities/stripe-subscription-schedule';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { ActivateTrialMembershipFormField, TRIAL_MEMBERSHIP_LENGTH_DAYS } from './const';
import { getTrialProducts } from './util';

interface ActivateTrialMembershipFormValues {
  [ActivateTrialMembershipFormField.PartnerProductId]: string;
  [ActivateTrialMembershipFormField.ActiveUntil]: string;
}

/**
 * One-time trial activation at Basic Support (1V-5895). Deliberately separate
 * from AddComplimentaryPeriodModal: the plan list is capped to the pilot's
 * memberships, the length is fixed, and submitting also stamps the address's
 * "Trial Membership Activated At" custom attribute, which is what hides the
 * action afterwards and what the Trial Eligible support policy conditions on.
 */
export const ActivateTrialMembershipModal = () => {
  const { closeModal } = useModalMethods(Modals.ActivateTrialMembership);
  const { isOpened, modalProps } = useModalState(Modals.ActivateTrialMembership);
  const { activateTrialMembership } = useStripeSubscriptionSchedule();

  const address = modalProps?.address;
  const { offeredProducts, loading: isLoadingOfferedProducts } = useOfferedProducts({
    brandId: address?.brandId,
    partnerId: address?.partnerId,
  });

  const trialProducts = useMemo(() => getTrialProducts(offeredProducts), [offeredProducts]);
  const productOptions = useMemo(
    () =>
      trialProducts.map((product) => ({
        id: product.partnerProductId,
        value: product.partnerProductId,
        label: formatMembershipName(product.displayName),
        testId: `activate-trial-membership-product-option-${product.partnerProductId}`,
      })),
    [trialProducts],
  );

  const expirationDate = useMemo(
    () => dayjs().add(TRIAL_MEMBERSHIP_LENGTH_DAYS, 'day'),
    // recompute per open so a long-lived tab still stamps "today + TRIAL_MEMBERSHIP_LENGTH_DAYS"
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpened],
  );

  const formMethods = useForm<ActivateTrialMembershipFormValues>({
    mode: 'onChange',
    values: {
      [ActivateTrialMembershipFormField.PartnerProductId]:
        trialProducts[0]?.partnerProductId ?? '',
      [ActivateTrialMembershipFormField.ActiveUntil]: expirationDate.format('MMM D, YYYY'),
    },
  });
  const { handleSubmit } = formMethods;

  const handleClose = useCallback(() => {
    formMethods.reset();
    closeModal();
  }, [closeModal, formMethods]);

  const onSubmit = useCallback(
    async (data: ActivateTrialMembershipFormValues) => {
      if (!address?.addressId || !address?.partnerId) {
        return;
      }

      const { addressId } = address;
      formMethods.reset();
      closeModal();

      // the backend enforces eligibility and writes the one-per-site stamp
      await activateTrialMembership({
        addressId,
        partnerId: address.partnerId,
        cpp: {
          partnerProductId: data.partnerProductId,
          expDate: expirationDate.format('YYYY-MM-DD'),
        },
      });
    },
    [activateTrialMembership, address, closeModal, expirationDate, formMethods],
  );

  const hasNoTrialProducts = !isLoadingOfferedProducts && !trialProducts.length;

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Activate trial membership"
      actions={[
        {
          label: 'Cancel',
          kind: 'secondary',
          testId: 'activate-trial-membership-cancel',
        },
        {
          label: 'Activate trial',
          kind: 'primary',
          onClick: handleSubmit(onSubmit),
          // both fields are prefilled and read-only, and isValid only settles after a field
          // event, so gating on it greyed the button out until the date was clicked (1V-6386)
          disabled: isLoadingOfferedProducts || !trialProducts.length,
          testId: 'activate-trial-membership-submit',
        },
      ]}
      onClose={handleClose}
      data-testid="activate-trial-membership-modal"
    >
      <GenericForm<ActivateTrialMembershipFormValues> formMethods={formMethods}>
        <Stack gap={0.5}>
          <FormSelect
            type="select"
            name={ActivateTrialMembershipFormField.PartnerProductId}
            label="Membership"
            required
            options={productOptions}
            // read-only while Essentials Plus is the only trial plan (1V-6166)
            disabled
            data-testid="activate-trial-membership-product"
          />
          {hasNoTrialProducts ? (
            <FormHelperText error data-testid="activate-trial-membership-no-products">
              This partner offers none of the pilot memberships
            </FormHelperText>
          ) : null}
        </Stack>
        <FormTextField
          type="textfield"
          name={ActivateTrialMembershipFormField.ActiveUntil}
          label="Active until"
          description={`One ${TRIAL_MEMBERSHIP_LENGTH_DAYS}-day trial per site, funded by the partner.`}
          textFieldProps={{
            InputProps: { readOnly: true },
            inputProps: { 'data-testid': 'activate-trial-membership-active-until' },
          }}
        />
      </GenericForm>
    </ModalContainer>
  );
};
